import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import DemoBanner from "@/demo/DemoBanner";
import DemoChat from "@/demo/DemoChat";
import BricksLoader from "@/components/BricksLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ThemeProvider } from "next-themes";
import { DEMO_MODE } from "@/lib/demoMode";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DispatchLayout from "@/components/layout/DispatchLayout";
import DeveloperProjectsPage from "@/pages/developer/DeveloperProjectsPage";
import DeveloperOverviewPage from "@/pages/developer/DeveloperOverviewPage";
import PropertyOpsPage from "@/pages/developer/PropertyOpsPage";
import DeveloperBookPage from "@/pages/developer/DeveloperBookPage";
import DeveloperBillingPage from "@/pages/developer/DeveloperBillingPage";
import { isPropertyKind } from "@/lib/clientTypes";
import DispatchTodayPage from "@/pages/dispatch/DispatchTodayPage";
import DispatchSchedulePage from "@/pages/dispatch/DispatchSchedulePage";
import CalendarPage from "@/pages/CalendarPage";
import DispatchPayoutsPage from "@/pages/dispatch/DispatchPayoutsPage";
import DispatchUploadsPage from "@/pages/dispatch/DispatchUploadsPage";
import DispatchAccountPage from "@/pages/dispatch/DispatchAccountPage";
import DispatchJobDetailPage from "@/pages/dispatch/DispatchJobDetailPage";
import DispatchNotificationsPage from "@/pages/dispatch/DispatchNotificationsPage";
import DispatchSettingsPage from "@/pages/dispatch/DispatchSettingsPage";
import DispatchProfileEditPage from "@/pages/dispatch/DispatchProfileEditPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import LogoutPage from "@/pages/LogoutPage";
import AddEmailPage from "@/pages/AddEmailPage";
import AcceptTermsPage from "@/pages/AcceptTermsPage";
import { needsTerms } from "@/lib/termsGate";
import { loginPathFor } from "@/lib/returnTo";
import GoogleCalendarCallback from "@/pages/GoogleCalendarCallback";
import OverviewPage from "@/pages/OverviewPage";
import OwnerOverviewPage from "@/pages/OwnerOverviewPage";
import OwnerSiteProgressPage from "@/pages/OwnerSiteProgressPage";
import ApplicantsPage from "@/pages/ApplicantsPage";
import InternalDocsPage from "@/pages/InternalDocsPage";
import ApplicantDetailPage from "@/pages/ApplicantDetailPage";
import BookingsPage from "@/pages/BookingsPage";
import AgentsPage from "@/pages/AgentsPage";
import PropertiesPage from "@/pages/PropertiesPage";
import BillingPage from "@/pages/BillingPage";
import VendorsPage from "@/pages/VendorsPage";
import SettingsPage from "@/pages/SettingsPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import SoloOverviewPage from "@/pages/solo/SoloOverviewPage";
import SoloBillingPage from "@/pages/solo/SoloBillingPage";
import SoloPropertiesPage from "@/pages/solo/SoloPropertiesPage";
import TeamOverviewPage from "@/pages/team/TeamOverviewPage";
import TeamMembersPage from "@/pages/team/TeamMembersPage";
import TeamBillingPage from "@/pages/team/TeamBillingPage";
import TeamPropertiesPage from "@/pages/team/TeamPropertiesPage";
import TransactionsPage from "@/pages/TransactionsPage";
import DealImportPage from "@/pages/DealImportPage";
import TransactionDetailPage from "@/pages/TransactionDetailPage";
import CRMPage from "@/pages/CRMPage";
import BoostPage from "@/pages/BoostPage";
import RecruitPage from "@/pages/RecruitPage";
import ProspectorsPage from "@/pages/ProspectorsPage";
import OutreachPage from "@/pages/OutreachPage";
import DocumentsPage from "@/pages/DocumentsPage";
import ESignPage from "@/pages/ESignPage";
import CompliancePage from "@/pages/CompliancePage";
import FormsPage from "@/pages/FormsPage";
import AccountingPage from "@/pages/AccountingPage";
import ReportsPage from "@/pages/ReportsPage";
import MLSPage from "@/pages/MLSPage";
import LaunchKitPage from "@/pages/LaunchKitPage";
import SigningPage from "@/pages/SigningPage";
import ClientSharePage from "@/pages/ClientSharePage";
import SiteSharePage from "@/pages/SiteSharePage";
import RoomPage from "@/pages/RoomPage";
import RemindersStopPage from "@/pages/RemindersStopPage";
import ProgressReportSharePage from "@/pages/ProgressReportSharePage";
import VisitPage from "@/pages/VisitPage";
import PropertyListingSitePage from "@/pages/PropertyListingSitePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import JobsPage from "@/pages/JobsPage";
import { workspaceKind } from "@/lib/workspace";
import { useEffect as useFinaleEffect } from "react";
import { legacyTarget } from "@/lib/legacyRoutes";
import { maybeRunFinaleOnArrival } from "@/lib/wallFinale";

function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const finaleUid = user && !user.needs_onboarding && location.pathname !== '/onboarding' ? (user.user_id || user.id) : null;
  useFinaleEffect(() => { if (finaleUid) maybeRunFinaleOnArrival(finaleUid); }, [finaleUid]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <BricksLoader variant="assemble" size={64} />
      </div>
    );
  }

  if (!user) return <Navigate to={loginPathFor(location)} state={{ from: location }} replace />;
  // Phone-OTP accounts confirm an email before any workspace route opens.
  if (user.needs_email) return <AddEmailPage />;
  // Every sign-in method (Google, Apple, passkey, phone, older accounts) agrees once.
  if (needsTerms(user)) return <AcceptTermsPage />;
  // account_type is the canonical vocabulary shared with Bricks-Companion;
  // role is kept as a fallback during the migration, not the primary check.
  const isPhotographer = user.account_type === 'vendor' || user.role === 'photographer';
  // Developer/REIT accounts don't go through the agent/brokerage onboarding
  // wizard (role/plan/subscription_type) — same bypass as photographers.
  const isDeveloper = isPropertyKind(user.account_type);
  if (!isPhotographer && !isDeveloper && user.needs_onboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}


const AGENT_BLOCKED = new Set(["/agents", "/billing", "/accounting", "/recruit", "/boost", "/mls"]);
const OWNER_ONLY = new Set(["/hiring", "/prospectors", "/outreach", "/owner", "/bookings", "/internal", "/site-progress"]);
const BUILDER_SHARED = new Set(["/settings"]);
function isOwnerOnlyPath(path) {
  if (OWNER_ONLY.has(path)) return true;
  if (path.startsWith("/hiring/")) return true;
  return false;
}

function RequireKind() {
  const { user } = useAuth();
  const location = useLocation();
  const kind = workspaceKind(user);
  if (kind !== "platform" && isOwnerOnlyPath(location.pathname)) {
    return <Navigate to="/" replace />;
  }
  if (kind === "agent" && AGENT_BLOCKED.has(location.pathname)) {
    return <Navigate to="/overview" replace />;
  }
  const isDevPath = location.pathname === "/developer" || location.pathname.startsWith("/developer/");
  const isBuilder = isPropertyKind(kind);
  if (isDevPath && !isBuilder) return <Navigate to="/" replace />;
  // Builder accounts use the Site progress shell; realtor desk pages aren't theirs.
  if (isBuilder && !isDevPath && !BUILDER_SHARED.has(location.pathname)) {
    return <Navigate to="/developer/overview" replace />;
  }
  return <Outlet />;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.is_platform_owner) return <Navigate to="/owner" replace />;
  if (user?.account_type === 'vendor' || user?.role === 'photographer') return <Navigate to="/dispatch" replace />;
  if (isPropertyKind(user?.account_type)) return <Navigate to="/developer/overview" replace />;
  const t = user?.subscription_type;
  if (t === "solo") return <Navigate to="/solo/overview" replace />;
  if (t === "team") return <Navigate to="/team/overview" replace />;
  return <Navigate to="/overview" replace />;
}

function LegacyRedirect() {
  const { pathname, search } = useLocation();
  const target = legacyTarget(pathname, search);
  useFinaleEffect(() => {
    if (target?.external) window.location.replace(target.external);
  }, [target?.external]);
  if (target?.to) return <Navigate to={target.to} replace />;
  return null;
}

function UnknownInApp() {
  // Unknown paths must not <Navigate> to RoleHome. That catch-all unmounted
  // listing-file calendar / e-sign dialogs whenever the URL missed a page
  // route (extra segments, overlay paths). Real role homes still live at "/".
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/auth/google-calendar" element={<GoogleCalendarCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public signing page — no auth required */}
      <Route path="/sign/:token" element={<SigningPage />} />
      {/* Public client media share + listing site — no auth */}
      <Route path="/share/:token" element={<ClientSharePage />} />
      {/* Public Site Progress timeline for a builder's GC / lender / asset manager — no auth */}
      <Route path="/site/:token" element={<SiteSharePage />} />
      <Route path="/room/:token" element={<RoomPage />} />
      <Route path="/reminders/stop/:token" element={<RemindersStopPage />} />
      {/* Public Site Progress report for a builder's lender / investors — no auth, link expires */}
      <Route path="/report/:token" element={<ProgressReportSharePage />} />
      <Route path="/v/:visitId" element={<VisitPage />} />
      <Route path="/listing/:token" element={<PropertyListingSitePage />} />
      {/* Old Companion / brokerage links redirect here path-for-path */}
      <Route path="/book" element={<LegacyRedirect />} />
      <Route path="/register" element={<LegacyRedirect />} />
      <Route path="/job/:id" element={<LegacyRedirect />} />
      <Route path="/inbox" element={<LegacyRedirect />} />
      <Route path="/disputes/:id" element={<LegacyRedirect />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* Dispatch shell — photographers only */}
        <Route element={<DispatchLayout />}>
          <Route path="/dispatch" element={<Navigate to="/dispatch/today" replace />} />
          <Route path="/dispatch/today" element={<DispatchTodayPage />} />
          <Route path="/dispatch/schedule" element={<DispatchSchedulePage />} />
          <Route path="/dispatch/upload" element={<DispatchUploadsPage />} />
          <Route path="/dispatch/payouts" element={<DispatchPayoutsPage />} />
          <Route path="/dispatch/account" element={<DispatchAccountPage />} />
        </Route>
        {/* Dispatch full-screen (no tab bar) */}
        <Route path="/dispatch/job/:id" element={<DispatchJobDetailPage />} />
        <Route path="/dispatch/notifications" element={<DispatchNotificationsPage />} />
        <Route path="/dispatch/settings" element={<DispatchSettingsPage />} />
        <Route path="/dispatch/profile-edit" element={<DispatchProfileEditPage />} />
        <Route element={<DashboardLayout />}>
          <Route element={<RequireKind />}>
          {/* Builder / developer (Site progress) — same shell, builder nav (lib/navConfig) */}
          <Route path="/developer" element={<Navigate to="/developer/overview" replace />} />
          <Route path="/developer/overview" element={<DeveloperOverviewPage />} />
          <Route path="/developer/projects" element={<DeveloperProjectsPage />} />
          <Route path="/developer/property/:id" element={<PropertyOpsPage />} />
          <Route path="/developer/book" element={<DeveloperBookPage />} />
          <Route path="/developer/billing" element={<DeveloperBillingPage />} />
          {/* Office + agent routes */}
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/owner" element={<OwnerOverviewPage />} />
          <Route path="/hiring" element={<ApplicantsPage />} />
          <Route path="/internal" element={<InternalDocsPage />} />
          <Route path="/hiring/:id" element={<ApplicantDetailPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/site-progress" element={<OwnerSiteProgressPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/properties" element={<PropertiesPage />} />
          <Route path="/properties/:id" element={<PropertyDetailPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />

          {/* TMS + CRM + MLS */}
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/transactions/import" element={<DealImportPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/transactions/:id" element={<TransactionDetailPage />} />
          <Route path="/transactions/:id/*" element={<TransactionDetailPage />} />
          <Route path="/crm" element={<CRMPage />} />
          <Route path="/boost" element={<BoostPage />} />
          <Route path="/recruit" element={<RecruitPage />} />
          <Route path="/prospectors" element={<ProspectorsPage />} />
          <Route path="/outreach" element={<OutreachPage />} />
          <Route path="/mls" element={<MLSPage />} />
          <Route path="/mls/:id/launch" element={<LaunchKitPage />} />

          {/* Documents + Forms + E-Sign */}
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/esign" element={<ESignPage />} />
          <Route path="/esign/*" element={<ESignPage />} />

          {/* Financials */}
          <Route path="/accounting" element={<AccountingPage />} />
          <Route path="/reports" element={<ReportsPage />} />

          {/* Solo routes */}
          <Route path="/solo/overview" element={<SoloOverviewPage />} />
          <Route path="/solo/properties" element={<SoloPropertiesPage />} />
          <Route path="/solo/billing" element={<SoloBillingPage />} />

          {/* Team routes */}
          <Route path="/team/overview" element={<TeamOverviewPage />} />
          <Route path="/team/members" element={<TeamMembersPage />} />
          <Route path="/team/properties" element={<TeamPropertiesPage />} />
          <Route path="/team/billing" element={<TeamBillingPage />} />
          <Route path="*" element={<UnknownInApp />} />
          </Route>
        </Route>
        <Route path="/" element={<RoleHome />} />
      </Route>
    </Routes>
  );
}

// A deal room party page (/room/...) and its reminder stop page are someone else's view, never the demo's sales chrome.
function DemoOnly({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith("/room/") || pathname.startsWith("/reminders/") ? null : children;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            {DEMO_MODE && <DemoOnly><DemoBanner /></DemoOnly>}
            <AppRouter />
            {DEMO_MODE && <DemoOnly><DemoChat /></DemoOnly>}
          </BrowserRouter>
          <Toaster position="top-right" style={{ zIndex: 9999 }} />
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;